/*
 * Live allocation state for the §7 orders screens. The my-api holder is the
 * SOLE source of truth for who holds a seat (fix e75108d); box-office
 * seatStatus is never inherited. Unconfigured/unavailable ⇒ honest notice +
 * unknown-as-unassigned with actions disabled — nothing invented (ruling A).
 */
import { useCallback, useEffect, useState } from 'react'
import { loadSession } from '../../auth/session'
import { useData } from '../../data/DataProvider'
import { fetchOrderAllocations, myApiBase, MyApiError, type OrderAllocations } from '../../data/myApi'
import type { Order, Ticket } from '../../data/types'
import { applyAllocations } from './ordersModel'

/**
 * Honest allocation-readability states (D11, CMT-2026-08-09-043). These are
 * DISTINCT so the notice copy never blames the build for a merely absent
 * session:
 *   'unconfigured' — no VITE_MY_API_BASE (genuine build gap)
 *   'signed-out'   — no live session, or a 401/403 from my-api (auth)
 *   'unavailable'  — network / 5xx (service down)
 */
export type AllocState = 'unconfigured' | 'signed-out' | 'loading' | 'ready' | 'unavailable'

/** Cold-start resilience (BOARDROOM-2026-09-01-193406): my-api Lambda
 *  cold-start reliably beat the first fetch on stage, landing users on the
 *  degraded notice. The initial load now retries TRANSIENT failures with
 *  backoff — attempt → 2s → attempt → 4s → attempt (~8s wall incl. request
 *  time) — holding state='loading' throughout; 'unavailable' only after
 *  genuine exhaustion. NOTE: there is no explicit FE timeout on this fetch
 *  (no AbortController anywhere in the myApi path) — the failure mode is the
 *  native fetch rejection / gateway 5xx, so retry is the whole fix. */
export const ALLOC_RETRY_DELAYS_MS = [2000, 4000]

/** Transient = worth retrying: a network-level rejection (fetch TypeError —
 *  no HTTP answer at all) or a 5xx. Any 4xx is a REAL answer (auth, missing
 *  order) and is NEVER retried. */
export function isTransientAllocFailure(err: unknown): boolean {
  return err instanceof MyApiError ? err.status >= 500 : true
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export interface OrderSeats {
  state: AllocState
  /** Tickets with my-api holders merged into ruling-B seats[]. */
  tickets: Ticket[]
  /** The raw my-api allocations read (null until 'ready') — carries the
   *  per-row ticketUnitId the cross-unit same-person warning needs
   *  (CMT-2026-08-29-026: same-UNIT duplicates are server-422-blocked;
   *  same-EVENT-other-unit repeats only warn). */
  allocations: OrderAllocations | null
  refresh: () => void
}

/** Allocation-merged seats for ONE order. */
export function useOrderSeats(order: Order | null): OrderSeats {
  const { ticketsForOrder, sessionEmail } = useData()
  const [state, setState] = useState<AllocState>('unconfigured')
  const [allocations, setAllocations] = useState<OrderAllocations | null>(null)
  const [tick, setTick] = useState(0)

  const orderId = order?.id ?? null
  useEffect(() => {
    const base = myApiBase()
    const session = loadSession()
    // Split the guard (D11): build gap ≠ signed-out ≠ nothing-to-load.
    if (!base) {
      setState('unconfigured')
      setAllocations(null)
      return
    }
    if (!session) {
      setState('signed-out')
      setAllocations(null)
      return
    }
    if (!orderId) {
      setState('unconfigured')
      setAllocations(null)
      return
    }
    let cancelled = false
    setState('loading')
    void (async () => {
      let lastErr: unknown
      // attempt 0, then one retry per backoff delay — TRANSIENT failures only
      // (BOARDROOM-2026-09-01-193406). A 4xx breaks out immediately: it is a
      // real answer, never retried.
      for (let attempt = 0; attempt <= ALLOC_RETRY_DELAYS_MS.length; attempt++) {
        if (attempt > 0) await sleep(ALLOC_RETRY_DELAYS_MS[attempt - 1])
        if (cancelled) return
        try {
          const a = await fetchOrderAllocations(session.idToken, base, orderId)
          if (cancelled) return
          setAllocations(a)
          setState('ready')
          return
        } catch (err: unknown) {
          lastErr = err
          if (!isTransientAllocFailure(err)) break
        }
      }
      if (cancelled) return
      setAllocations(null)
      // A 401/403 is an auth failure (expired/absent session), NOT the
      // service being down — route it to the sign-in notice (D11).
      const status = lastErr instanceof MyApiError ? lastErr.status : undefined
      setState(status === 401 || status === 403 ? 'signed-out' : 'unavailable')
    })()
    return () => { cancelled = true }
  }, [orderId, tick])

  const refresh = useCallback(() => setTick((t) => t + 1), [])
  const base = orderId ? ticketsForOrder(orderId) : []
  return {
    state,
    tickets: applyAllocations(base, state === 'ready' ? allocations : null, sessionEmail),
    allocations: state === 'ready' ? allocations : null,
    refresh,
  }
}
